/* eslint-disable -- UX wireframe; not held to app lint rules */
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { T, useTheme } from "../../../tokens";
import { Flowchart } from "../../../shared/Flowchart";
import { InspectProvider, InspectorPanel, InspectorToolbar, useInspector } from "../../../shared/Inspector";
import WizardChrome from "../../../shared/WizardChrome";
import ViewportFrame from "../../../shared/ViewportFrame";
import { canvasHeightForViewport } from "../../../shared/layoutTokens";
import { WIZARD_TABS, ISSUES_TAB } from "../../../shared/wizardTabs";
import { buildFlowContext } from "../ziniHubScreenSpecs";
import { ScreenWF } from "./screenRegistry";
import {
  REG_STEPS,
  BIZ_CARD_INDICATORS,
  NEW_BIZ_SETTINGS_FIELDS,
} from "./zinihubAdminFlows";

const REGISTER_STEP_IDS = new Set(["admin-register", "register-s2", "register-s3"]);
const BIZ_CARD_STEP_IDS = new Set(["biz-settings", "biz-grid", "biz-preview", "biz-page"]);

function ViewToggle({ value, onChange, th }) {
  const opts = [{ id: "list", label: "List" }, { id: "diagram", label: "Diagram" }];
  return (
    <div style={{ display: "inline-flex", background: th.muted, border: `1px solid ${th.border}`, borderRadius: 14, padding: 2 }}>
      {opts.map((opt) => {
        const active = value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onChange(opt.id)}
            style={{
              background: active ? th.surface : "transparent",
              border: "none",
              borderRadius: 12,
              padding: "3px 10px",
              fontSize: 10,
              fontWeight: 600,
              color: active ? T.brand : th.textSec,
              cursor: "pointer",
              transition: th.tr,
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function RouteBadge({ label, type }) {
  const styles = {
    confirmed: { bg: "rgba(59,130,246,0.15)", color: "#60a5fa" },
    neutral: { bg: "rgba(128,128,128,0.1)", color: "rgba(128,128,128,0.55)" },
  };
  const style = styles[type] || styles.neutral;
  return (
    <span
      style={{
        background: style.bg,
        color: style.color,
        borderRadius: 5,
        padding: "2px 7px",
        fontSize: 10,
        fontWeight: 500,
        border: `1px solid ${style.color}22`,
      }}
    >
      {label}
    </span>
  );
}

function FolderNode({ node, depth, th }) {
  const pad = 8 + depth * 14;
  if (node.type === "file") {
    return (
      <div style={{ padding: `3px 0 3px ${pad}px`, fontSize: 10, color: th.textSec, display: "flex", gap: 8 }}>
        <span style={{ color: T.cyan, fontFamily: "monospace" }}>{node.name}</span>
        {node.note && <span style={{ color: th.textMuted, fontSize: 9 }}>— {node.note}</span>}
      </div>
    );
  }
  return (
    <div>
      <div style={{ padding: `4px 0 4px ${pad}px`, fontSize: 10, fontWeight: 600, color: T.amber }}>{node.name}</div>
      {(node.children || []).map((child, index) => (
        <FolderNode key={`${child.name}-${index}`} node={child} depth={depth + 1} th={th} />
      ))}
    </div>
  );
}

function RegisterAppendix({ dark, viewport, th }) {
  return (
    <div style={{ marginTop: 24, maxWidth: 900 }}>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Register wizard — field spec</div>
      <div style={{ color: th.textSec, fontSize: 12, marginBottom: 12 }}>
        Gender removed from registration. DOB optional in step 3. Same component on admin and customer domains.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 16 }}>
        {REG_STEPS.map((step) => (
          <div key={step.step} style={{ background: th.surface, border: `1px solid ${th.border}`, borderRadius: T.rlg, overflow: "hidden" }}>
            <div style={{ padding: "9px 12px", borderBottom: `1px solid ${th.border}`, background: T.brandSoft }}>
              <div style={{ fontSize: 9, color: T.brand, fontWeight: 600 }}>Step {step.step} of 3</div>
              <div style={{ fontSize: 12, fontWeight: 600 }}>{step.title}</div>
            </div>
            <div style={{ padding: 12, fontSize: 10, color: th.textSec }}>Gate: {step.gate}</div>
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 10 }}>
        {["admin-register", "register-s2", "register-s3"].map((screenId, index) => (
          <div key={screenId}>
            <div style={{ fontSize: 10, color: th.textMuted, marginBottom: 5, textAlign: "center" }}>Step {index + 1}</div>
            <ScreenWF id={screenId} dark={dark} viewport={viewport} slim />
          </div>
        ))}
      </div>
    </div>
  );
}

function BizCardAppendix({ dark, viewport, th }) {
  return (
    <div style={{ marginTop: 24, maxWidth: 860 }}>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>Biz card indicators</div>
      <div style={{ background: th.surface, border: `1px solid ${th.border}`, borderRadius: T.rlg, overflow: "hidden", marginBottom: 16 }}>
        {BIZ_CARD_INDICATORS.map((indicator, index) => (
          <div
            key={indicator.name}
            style={{
              display: "grid",
              gridTemplateColumns: "140px 120px 1fr 1fr",
              gap: 0,
              padding: "9px 14px",
              borderBottom: index < BIZ_CARD_INDICATORS.length - 1 ? `1px solid ${th.border}` : "none",
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 500 }}>{indicator.name}</div>
            <div style={{ fontSize: 10, color: T.cyan, fontFamily: "monospace" }}>{indicator.badge || "—"}</div>
            <div style={{ fontSize: 10, color: th.textSec }}>{indicator.admin || "—"}</div>
            <div style={{ fontSize: 10, color: th.textSec }}>{indicator.cust || "—"}</div>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>New admin biz settings fields</div>
      <div style={{ background: th.surface, border: `1px solid ${th.border}`, borderRadius: T.rlg, overflow: "hidden", marginBottom: 16 }}>
        {NEW_BIZ_SETTINGS_FIELDS.map((fieldRow, index) => (
          <div
            key={fieldRow.field}
            style={{
              padding: "10px 14px",
              borderBottom: index < NEW_BIZ_SETTINGS_FIELDS.length - 1 ? `1px solid ${th.border}` : "none",
              fontSize: 10,
              color: th.textSec,
            }}
          >
            <strong style={{ color: th.text }}>{fieldRow.field}</strong> — {fieldRow.type}. {fieldRow.note}
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <ScreenWF id="biz-settings" dark={dark} viewport={viewport} slim />
        <ScreenWF id="biz-page" dark={dark} viewport={viewport} slim />
      </div>
    </div>
  );
}

function ZiniHubWizardInner({ appConfig }) {
  const {
    id: appId,
    title,
    domain,
    subtitle,
    flows,
    routes,
    ia,
    shells,
    shellPreviews,
    issues,
    accentColor,
    folderStructure,
    techStack,
    configs,
  } = appConfig;

  const [searchParams] = useSearchParams();
  const showIssues = searchParams.get("review") === "1";

  const [dark, setDark] = useState(true);
  const [section, setSection] = useState("flows");
  const [viewport, setViewport] = useState("desktop");
  const [activeFlow, setActiveFlow] = useState(null);
  const [flowStep, setFlowStep] = useState(0);
  const [activeIssue, setActiveIssue] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [flowsView, setFlowsView] = useState("list");
  const [iaView, setIaView] = useState("list");
  const { mode, setMode } = useInspector();
  const th = useTheme(dark);

  const sections = useMemo(() => {
    const tabs = [...WIZARD_TABS];
    if (showIssues) tabs.push(ISSUES_TAB);
    return tabs;
  }, [showIssues]);

  const sev = {
    bug: { icon: "🐛", c: T.red, bg: dark ? T.redSoft : "rgba(239,68,68,0.06)" },
    ux: { icon: "🎯", c: T.amber, bg: dark ? T.amberSoft : "rgba(245,158,11,0.06)" },
    visual: { icon: "🎨", c: T.brand, bg: dark ? T.brandSoft : "rgba(94,76,226,0.06)" },
    interaction: { icon: "✨", c: T.cyan, bg: dark ? T.cyanSoft : "rgba(6,182,212,0.06)" },
    responsive: { icon: "📱", c: T.teal, bg: dark ? T.tealSoft : "rgba(20,184,166,0.06)" },
  };

  const currentFlow = flows.find((flow) => flow.id === activeFlow) || null;
  const currentStep = (currentFlow && currentFlow.steps[flowStep]) || null;
  const flowContext =
    currentFlow && currentStep ? buildFlowContext({ flow: currentFlow, step: currentStep, screenId: currentStep.id }) : null;
  const inspectorOpen = mode !== "off";
  const showFlowSidebar = sidebarOpen && (section === "flows" || section === "ia");

  const flowSidebar =
    section === "flows" ? (
      <>
        {flows.map((flow) => (
          <div
            key={flow.id}
            role="button"
            tabIndex={0}
            onClick={() => {
              setActiveFlow(flow.id);
              setFlowStep(0);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              padding: "6px 12px",
              cursor: "pointer",
              background: activeFlow === flow.id ? "rgba(94,76,226,0.08)" : "none",
              borderLeft: activeFlow === flow.id ? `2px solid ${flow.color}` : "2px solid transparent",
            }}
          >
            <div style={{ width: 5, height: 5, borderRadius: "50%", background: flow.color, flexShrink: 0 }} />
            <span style={{ fontSize: 11, color: activeFlow === flow.id ? flow.color : th.textSec }}>{flow.label}</span>
            <span style={{ marginLeft: "auto", fontSize: 9, color: th.textMuted }}>{flow.steps.length}</span>
          </div>
        ))}
      </>
    ) : (
      <>
        {ia.map((group) => (
          <div key={group.id}>
            <div
              role="button"
              tabIndex={0}
              onClick={() => setExpandedGroup(expandedGroup === group.id ? null : group.id)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "5px 12px",
                cursor: "pointer",
                background: expandedGroup === group.id ? `${accentColor}15` : "none",
                borderLeft: expandedGroup === group.id ? `2px solid ${accentColor}` : "2px solid transparent",
              }}
            >
              <span style={{ fontSize: 12 }}>{group.icon}</span>
              <span style={{ fontSize: 11, fontWeight: expandedGroup === group.id ? 500 : 400 }}>{group.label}</span>
              <span style={{ marginLeft: "auto", fontSize: 9, color: th.textMuted }}>{group.screens.length}</span>
            </div>
            {expandedGroup === group.id &&
              group.screens.map((screen) => (
                <div key={screen.id} style={{ padding: "4px 12px 4px 28px", fontSize: 10, color: th.textSec }}>
                  {screen.bug && <span style={{ color: T.red, marginRight: 4 }}>bug</span>}
                  {screen.label}
                </div>
              ))}
          </div>
        ))}
      </>
    );

  const inspectorPanel = inspectorOpen ? (
    <InspectorPanel dark={dark} embedded flowContext={flowContext} onClose={() => setMode("off")} />
  ) : null;

  return (
    <WizardChrome
      dark={dark}
      setDark={setDark}
      title={title}
      subtitle={subtitle}
      domain={domain}
      sections={sections}
      section={section}
      onSectionChange={(next) => {
        setSection(next);
        setActiveFlow(null);
      }}
      viewport={viewport}
      onViewportChange={setViewport}
      sidebarOpen={sidebarOpen}
      onSidebarToggle={() => setSidebarOpen((value) => !value)}
      flowSidebar={showFlowSidebar ? flowSidebar : null}
      inspectorPanel={inspectorPanel}
      inspectorOpen={inspectorOpen}
    >
      <div style={{ marginBottom: 12, fontSize: 11, color: th.textMuted }}>
        Brand tokens:{" "}
        <Link to="/brand-book" style={{ color: T.brand }}>
          Brand Book →
        </Link>
      </div>

      {section === "flows" && !currentFlow && (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
            <div style={{ fontSize: 18, fontWeight: 600 }}>Flows</div>
            <div style={{ flex: 1 }} />
            <ViewToggle value={flowsView} onChange={setFlowsView} th={th} />
          </div>
          <div style={{ color: th.textSec, fontSize: 12, marginBottom: 20 }}>
            {flows.length} flows for {appId === "admin" ? "merchant admin" : "customer app"}. Open sidebar or pick a card.
          </div>
          {flowsView === "list" && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 10 }}>
              {flows.map((flow) => (
                <div
                  key={flow.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    setActiveFlow(flow.id);
                    setFlowStep(0);
                  }}
                  style={{
                    background: th.surface,
                    border: `1px solid ${th.border}`,
                    borderRadius: T.rlg,
                    padding: 16,
                    cursor: "pointer",
                    borderLeft: `3px solid ${flow.color}`,
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>{flow.label}</div>
                  <div style={{ fontSize: 11, color: th.textSec }}>{flow.steps.length} steps</div>
                </div>
              ))}
            </div>
          )}
          {flowsView === "diagram" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {flows.map((flow) => (
                <Flowchart
                  key={flow.id}
                  dark={dark}
                  mode="linear"
                  title={flow.label}
                  accent={flow.color}
                  groups={[
                    {
                      id: flow.id,
                      label: flow.label,
                      items: flow.steps.map((step) => ({ id: step.id, label: step.label, sublabel: step.shell })),
                    },
                  ]}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {section === "flows" && currentFlow && currentStep && (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
            <button
              type="button"
              onClick={() => setActiveFlow(null)}
              style={{
                background: "none",
                border: `1px solid ${th.border}`,
                borderRadius: 7,
                padding: "5px 10px",
                cursor: "pointer",
                color: th.textSec,
                fontSize: 11,
              }}
            >
              ← Flows
            </button>
            <div style={{ fontWeight: 600, fontSize: 15 }}>{currentFlow.label}</div>
          </div>
          <div style={{ display: "flex", gap: 4, marginBottom: 18, flexWrap: "wrap" }}>
            {currentFlow.steps.map((step, index) => (
              <button
                key={step.id}
                type="button"
                onClick={() => setFlowStep(index)}
                style={{
                  background: index === flowStep ? currentFlow.color : th.muted,
                  border: "none",
                  borderRadius: 6,
                  padding: "4px 9px",
                  cursor: "pointer",
                  color: index === flowStep ? "#fff" : th.textMuted,
                  fontSize: 10,
                }}
              >
                {index + 1}. {step.label}
              </button>
            ))}
          </div>
          <div style={{ background: th.surface, border: `2px solid ${currentFlow.color}44`, borderRadius: T.rlg, overflow: "hidden", marginBottom: 10, display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "8px 14px", borderBottom: `1px solid ${th.border}`, fontWeight: 600, fontSize: 13, flexShrink: 0 }}>
              {currentStep.label}
              <span style={{ fontSize: 9, color: th.textMuted, marginLeft: 8 }}>{currentStep.shell}</span>
            </div>
            <ViewportFrame
              viewport={viewport}
              dark={dark}
              minCanvasHeight={canvasHeightForViewport(viewport)}
              style={{ flex: 1, minHeight: canvasHeightForViewport(viewport) }}
            >
              <ScreenWF id={currentStep.id} dark={dark} viewport={viewport} slim={false} appId={appId} shell={currentStep.shell} />
            </ViewportFrame>
          </div>
          <div style={{ display: "flex", gap: 7 }}>
            <button
              type="button"
              disabled={flowStep === 0}
              onClick={() => setFlowStep((value) => Math.max(0, value - 1))}
              style={{
                padding: "6px 16px",
                fontSize: 11,
                borderRadius: 7,
                border: `1px solid ${th.borderMd}`,
                background: th.surface,
                color: th.text,
                opacity: flowStep === 0 ? 0.45 : 1,
                cursor: flowStep === 0 ? "default" : "pointer",
              }}
            >
              ← Previous
            </button>
            <button
              type="button"
              disabled={flowStep >= currentFlow.steps.length - 1}
              onClick={() => setFlowStep((value) => Math.min(currentFlow.steps.length - 1, value + 1))}
              style={{
                padding: "6px 16px",
                fontSize: 11,
                borderRadius: 7,
                border: "none",
                background: currentFlow.color,
                color: "#fff",
                opacity: flowStep >= currentFlow.steps.length - 1 ? 0.3 : 1,
              }}
            >
              Next →
            </button>
          </div>
          {currentFlow.appendix === "register" && REGISTER_STEP_IDS.has(currentStep.id) && <RegisterAppendix dark={dark} viewport={viewport} th={th} />}
          {currentFlow.appendix === "biz-card" && BIZ_CARD_STEP_IDS.has(currentStep.id) && <BizCardAppendix dark={dark} viewport={viewport} th={th} />}
        </div>
      )}

      {section === "ia" && (
        <div style={{ maxWidth: iaView === "diagram" ? "100%" : 760 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
            <div style={{ fontSize: 18, fontWeight: 600 }}>Information Architecture</div>
            <div style={{ flex: 1 }} />
            <ViewToggle value={iaView} onChange={setIaView} th={th} />
          </div>
          {iaView === "diagram" && (
            <Flowchart
              dark={dark}
              mode="board"
              title={title}
              accent={accentColor}
              groups={ia.map((group) => ({
                id: group.id,
                label: `${group.icon || ""} ${group.label}`.trim(),
                color: accentColor,
                items: group.screens.map((screen) => ({
                  id: screen.id,
                  label: screen.label,
                  sublabel: screen.tag || "",
                  badge: screen.bug ? "bug" : undefined,
                })),
              }))}
            />
          )}
          {iaView === "list" && (
            <div style={{ background: th.surface, border: `1px solid ${th.border}`, borderRadius: T.rlg, overflow: "hidden" }}>
              {ia.map((group) => (
                <div key={group.id} style={{ borderBottom: `1px solid ${th.border}` }}>
                  <div style={{ padding: "8px 14px", background: th.muted, fontWeight: 600, fontSize: 12 }}>
                    {group.icon} {group.label}
                  </div>
                  {group.screens.map((screen) => (
                    <div key={screen.id} style={{ padding: "5px 14px 5px 28px", fontSize: 11, color: th.textSec, display: "flex", gap: 6 }}>
                      {screen.bug && <span style={{ fontSize: 8, color: T.red }}>bug</span>}
                      <span>{screen.label}</span>
                      {screen.tag && <span style={{ marginLeft: "auto", fontSize: 9, color: th.textMuted }}>{screen.tag}</span>}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {section === "routes" && (
        <div style={{ maxWidth: 900 }}>
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Route map</div>
          <div style={{ background: th.surface, border: `1px solid ${th.border}`, borderRadius: T.rlg, overflow: "hidden" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "160px 150px 120px 70px 1fr",
                padding: "6px 14px",
                background: th.muted,
                fontSize: 9,
                fontWeight: 600,
                color: th.textMuted,
                textTransform: "uppercase",
              }}
            >
              {["Path", "Component", "Shell", "Auth", "Notes"].map((header) => (
                <div key={header}>{header}</div>
              ))}
            </div>
            {routes.map((route, index) => (
              <div
                key={route.path}
                style={{
                  display: "grid",
                  gridTemplateColumns: "160px 150px 120px 70px 1fr",
                  padding: "8px 14px",
                  borderBottom: index < routes.length - 1 ? `1px solid ${th.border}` : "none",
                  alignItems: "center",
                }}
              >
                <div style={{ fontFamily: "monospace", fontSize: 10, color: T.cyan }}>{route.path}</div>
                <div style={{ fontFamily: "monospace", fontSize: 10, color: T.brand }}>{route.comp}</div>
                <div style={{ fontSize: 10, color: th.textSec }}>{route.shell}</div>
                <RouteBadge label={route.auth} type={route.auth === "protected" ? "confirmed" : "neutral"} />
                <div style={{ fontSize: 10, color: th.textMuted, lineHeight: 1.5 }}>{route.note}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {section === "folders" && folderStructure && (
        <div style={{ maxWidth: 720 }}>
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Folder structure</div>
          <div style={{ background: th.surface, border: `1px solid ${th.border}`, borderRadius: T.rlg, padding: 16 }}>
            <div style={{ fontSize: 11, color: T.amber, fontWeight: 600, marginBottom: 10 }}>📁 {folderStructure.root}</div>
            {folderStructure.tree.map((node, index) => (
              <FolderNode key={`${node.name}-${index}`} node={node} depth={0} th={th} />
            ))}
          </div>
        </div>
      )}

      {section === "shells" && (
        <div style={{ maxWidth: 860 }}>
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Shell specs</div>
          {Object.entries(shells).map(([shellName, shell]) => (
            <div key={shellName} style={{ background: th.surface, border: `1px solid ${th.border}`, borderRadius: T.rlg, marginBottom: 14, overflow: "hidden" }}>
              <div style={{ padding: "10px 16px", borderBottom: `1px solid ${th.border}`, fontFamily: "monospace", fontWeight: 600, color: T.brand }}>
                {shellName}
                <span style={{ fontFamily: "sans-serif", fontWeight: 400, color: th.textSec, marginLeft: 8, fontSize: 10 }}>{shell.who}</span>
              </div>
              <div style={{ padding: 14, fontSize: 11, color: th.textSec, lineHeight: 1.6 }}>
                {shell.note && <p style={{ margin: "0 0 10px", color: T.amber }}>⚠️ {shell.note}</p>}
                {shell.footer && <p>Footer: {shell.footer}</p>}
                {shell.cart && <p>Cart: {shell.cart}</p>}
              </div>
              <div style={{ padding: "0 14px 14px" }}>
                <ScreenWF id={shellPreviews[shellName] || "order-monitoring"} dark={dark} viewport={viewport} slim />
              </div>
            </div>
          ))}
        </div>
      )}

      {section === "tech-stack" && techStack && (
        <div style={{ maxWidth: 640 }}>
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Tech stack</div>
          <div style={{ background: th.surface, border: `1px solid ${th.border}`, borderRadius: T.rlg, overflow: "hidden" }}>
            {techStack.map((row, index) => (
              <div
                key={row.layer}
                style={{
                  display: "grid",
                  gridTemplateColumns: "100px 1fr 1.5fr",
                  gap: 12,
                  padding: "10px 14px",
                  borderBottom: index < techStack.length - 1 ? `1px solid ${th.border}` : "none",
                  fontSize: 11,
                }}
              >
                <div style={{ fontWeight: 600, color: accentColor }}>{row.layer}</div>
                <div style={{ fontFamily: "monospace", color: th.text }}>{row.tech}</div>
                <div style={{ color: th.textMuted }}>{row.note}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {section === "configs" && configs && (
        <div style={{ maxWidth: 640 }}>
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 12 }}>Config keys</div>
          <div style={{ background: th.surface, border: `1px solid ${th.border}`, borderRadius: T.rlg, overflow: "hidden" }}>
            {configs.map((row, index) => (
              <div
                key={row.key}
                style={{
                  padding: "10px 14px",
                  borderBottom: index < configs.length - 1 ? `1px solid ${th.border}` : "none",
                  fontSize: 11,
                }}
              >
                <div style={{ fontFamily: "monospace", color: T.cyan, fontWeight: 600 }}>{row.key}</div>
                <div style={{ color: th.textMuted, fontSize: 10 }}>{row.where}</div>
                <div style={{ color: th.textSec, marginTop: 4 }}>{row.note}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {section === "issues" && showIssues && (
        <div style={{ maxWidth: 860 }}>
          <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 6 }}>Issues + UX solutions</div>
          <div style={{ color: th.textSec, fontSize: 12, marginBottom: 14 }}>
            {issues.length} issues · add <code>?review=1</code> to URL to show this tab
          </div>
          {issues.map((issue) => {
            const severity = sev[issue.sev] || sev.ux;
            const open = activeIssue === issue.id;
            return (
              <div
                key={issue.id}
                style={{
                  background: severity.bg,
                  border: `1px solid ${severity.c}22`,
                  borderRadius: T.rlg,
                  borderLeft: `3px solid ${severity.c}`,
                  marginBottom: 8,
                  overflow: "hidden",
                }}
              >
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setActiveIssue(open ? null : issue.id)}
                  style={{ padding: "10px 14px", cursor: "pointer" }}
                >
                  <div style={{ fontSize: 12, fontWeight: 500 }}>{issue.problem}</div>
                  <div style={{ fontSize: 10, color: th.textMuted, marginTop: 4 }}>
                    {issue.screen} · {issue.route}
                  </div>
                </div>
                {open && (
                  <div style={{ padding: "0 14px 14px", borderTop: `1px solid ${severity.c}22` }}>
                    <div style={{ fontSize: 12, color: th.textSec, lineHeight: 1.7, marginTop: 10 }}>{issue.solution}</div>
                    <div style={{ fontSize: 11, color: T.brand, fontFamily: "monospace", marginTop: 8, background: T.brandSoft, padding: 8, borderRadius: 6 }}>
                      {issue.pattern}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </WizardChrome>
  );
}

/** Parameterized ZiniHub UX wizard (admin or customer). */
export default function ZiniHubWizardApp({ appConfig }) {
  return (
    <InspectProvider>
      <ZiniHubWizardInner appConfig={appConfig} />
    </InspectProvider>
  );
}
